// EDEN · curriculum-as-data client (flex spec §1-2; SCHEMA.md frozen 2026-07-03).
//
// The compiled grade files (`curriculum/g{n}.json`) are static JSON, versioned with the repo —
// no DB, no seeding. Vite code-splits them via import.meta.glob, so a grade's data loads lazily
// the first time a screen asks for it and is cached after. The SEQUENCE is the truth; weeks are
// suggestions (nodes, not dates). Position here is TEMPLATE position (term + suggested_week):
// per-class position via lesson.node_id lands with migration 0016 (increment 3).

export interface NodeCoursebook {
  book?: string;
  unit: number | string | null;
  lessons: string | null;
  grammar: string | null;
  vocabulary?: string | null;
}

export interface NodeThread {
  vocab_set: string | null;
  task_family: string | null;
  bloom: string | null;
  grammar_micro?: string;
}

export interface NodeFluency {
  activity: string;
  topic?: string;
  reader_band?: string;
}

export interface CurriculumNode {
  id: string;
  seq: number;
  term: number;
  suggested_week: number;
  type: "teaching" | "review" | "anchor" | "buffer";
  title: string;
  coursebook?: NodeCoursebook | null;
  objectives?: string[];
  thread?: NodeThread;
  fluency?: NodeFluency;
  reading?: string;
  events: string[];
  notes?: string;
}

export interface CurriculumAnchor {
  id: string;
  kind: "mock" | "gate" | "sa" | "registration" | "event";
  label: string;
  window: { term: number; weeks: [number, number] };
}

export interface GradeCurriculum {
  version: string;
  grade: number;
  coursebook: string;
  cefr_arc: string;
  exam: string;
  gate: boolean;
  rhythm: { periods_per_week: number; coursebook: number; thread: number; fluency: number };
  coverage: { floor: number; countable_types: string[]; countable_total: number };
  nodes: CurriculumNode[];
  anchors: CurriculumAnchor[];
}

export interface VocabSet {
  id: string;
  title: string;
  rides_with: string;
  count: number;
  words: string[];
  note?: string;
}

export interface VocabRegister {
  grade: number;
  sets: VocabSet[];
}

// Lazy, code-split loaders. Keys look like "../curriculum/g6.json".
const gradeModules = import.meta.glob<GradeCurriculum>("../curriculum/g*.json", {
  import: "default",
});
const vocabModules = import.meta.glob<VocabRegister>("../curriculum/vocab/g*-sets.json", {
  import: "default",
});

const gradeCache = new Map<number, GradeCurriculum | null>();
const vocabCache = new Map<number, VocabRegister | null>();

/** The compiled curriculum for a grade, or null when none exists (e.g. G11-12 pending). */
export async function loadGrade(grade: number): Promise<GradeCurriculum | null> {
  if (gradeCache.has(grade)) return gradeCache.get(grade) ?? null;
  const loader = gradeModules[`../curriculum/g${grade}.json`];
  const data = loader ? await loader() : null;
  gradeCache.set(grade, data);
  return data;
}

/** The vocab set register for a grade (for set titles/counts on the node card). */
export async function loadVocab(grade: number): Promise<VocabRegister | null> {
  if (vocabCache.has(grade)) return vocabCache.get(grade) ?? null;
  const loader = vocabModules[`../curriculum/vocab/g${grade}-sets.json`];
  const data = loader ? await loader() : null;
  vocabCache.set(grade, data);
  return data;
}

/** Grade number from a class ("G6A" → 6, "grade 10 B" → 10), name first, then level. */
export function gradeFromClass(cls: { name: string; level: string | null }): number | null {
  for (const s of [cls.name, cls.level ?? ""]) {
    const m = /(?:^|[^0-9])g(?:rade)?\s*(\d{1,2})/i.exec(s);
    if (m) {
      const n = Number(m[1]);
      if (n >= 1 && n <= 12) return n;
    }
  }
  return null;
}

/** Term NUMBER (1..3) from the term row. The term name carries it ("… · Term 1"); anchor on the
 *  word "Term" so a year in the name ("AY2025/26 · Term 3") isn't mis-read as the number (the bare
 *  /([123])/ matched the "2" in "2025"). Order-of-dates is not available here, so an unparseable
 *  name returns null and screens say so calmly. */
export function termNumber(term: { name: string }): number | null {
  const m = /\bterm\s*([123])\b/i.exec(term.name);
  return m ? Number(m[1]) : null;
}

/** The year plan's lesson for a school week. Owner ruling A2 (21 Oct 2026): "this week" is the school
 *  calendar's week, the same for every teacher and every class of a grade — it is no longer "the class's position
 *  + 1" (the 2 July rule of flex spec §2 and SCHEMA.md, which this replaces). Exact when the grade has a node for
 *  that term and week; otherwise the nearest EARLIER node by sequence, with `exact: false` so a page can say
 *  "no new lesson in the year plan this week: catch up or review". The week comes from `term` and `suggested_week`,
 *  never from a node id (`G1-T2-12` is Week 15). */
export function schoolWeekNode(
  cur: GradeCurriculum,
  term: number,
  week: number,
): { node: CurriculumNode | null; exact: boolean } {
  let best: CurriculumNode | null = null;
  for (const n of cur.nodes) {
    if (n.term === term && n.suggested_week === week) return { node: n, exact: true };
    if (n.term < term || (n.term === term && n.suggested_week < week)) {
      if (!best || n.seq > best.seq) best = n;
    }
  }
  return { node: best ?? cur.nodes[0] ?? null, exact: false };
}

/** The template node for a term-week: exact match, else the nearest earlier node. */
export function nodeForWeek(
  cur: GradeCurriculum,
  term: number,
  week: number,
): CurriculumNode | null {
  return schoolWeekNode(cur, term, week).node;
}

/** Countable progress at a node (coverage denominator = teaching + review, flex spec §1). */
export function countableProgress(
  cur: GradeCurriculum,
  at: CurriculumNode | null,
): { done: number; total: number } {
  const total = cur.coverage.countable_total;
  if (!at) return { done: 0, total };
  let done = 0;
  for (const n of cur.nodes) {
    if (n.seq <= at.seq && (n.type === "teaching" || n.type === "review")) done += 1;
  }
  return { done, total };
}

/** Resolve a node's vocab_set against the register. Split sets are referenced by BASE id
 *  (SCHEMA.md rebalance rule): "STA-13" may resolve to STA-13-a + STA-13-b. Retrieval tokens
 *  (-R / RETRIEVAL-* / WEAK-SETS / TARGETED / *-00) resolve to null — the card shows them as-is. */
export function resolveSets(reg: VocabRegister | null, vocabSet: string | null): VocabSet[] {
  if (!reg || !vocabSet) return [];
  if (/^(RETRIEVAL|WEAK|TARGETED|KEY-PREVIEW|IELTS-BRIDGE)/.test(vocabSet)) return [];
  if (vocabSet.endsWith("-R")) return [];
  const exact = reg.sets.filter((s) => s.id === vocabSet);
  if (exact.length) return exact;
  return reg.sets.filter((s) => s.id.startsWith(vocabSet + "-"));
}

/** One line for the node card's Thread row: `FLY-05 “Time” · 37 words` (parts summed). */
export function threadLine(reg: VocabRegister | null, thread: NodeThread | undefined): string {
  if (!thread) return "";
  const vs = thread.vocab_set;
  if (!vs) return thread.grammar_micro ? `grammar micro-lesson · ${thread.grammar_micro}` : "";
  const sets = resolveSets(reg, vs);
  if (!sets.length) return vs; // retrieval / weak-focus / intro tokens read fine raw
  const words = sets.reduce((a, s) => a + s.count, 0);
  const title = sets[0]?.title.replace(/\s*\([a-z]\)$/, "") ?? "";
  const label = words > 0 ? `${words} words` : "evidence-driven";
  return `${vs} “${title}” · ${label}${thread.grammar_micro ? ` · + ${thread.grammar_micro}` : ""}`;
}

// ── per-class position (increment 3: lessons carry node_id) ─────────────────────────────────

/** One row of GET /api/classes/:id/position — a node this class has planned against. */
export interface NodePlanState {
  node_id: string;
  planned: number;
  approved: number;
  /** Lessons for this node the class teacher currently confirms as TAUGHT (0027, JOB-W01).
   *  Optional so older payloads still parse; a session count, never a coverage claim. */
  taught?: number;
}

export interface ClassPosition {
  /** The furthest node with a linked lesson — the class's REAL position (flex spec §1). */
  position: CurriculumNode | null;
  /** seq of every node with at least one linked lesson (drawn moss on the roadmap). */
  planned: Set<number>;
  /** seq of every node with an approved linked lesson (the coverage proxy). */
  approved: Set<number>;
  /** seq of every node with at least one lesson the teacher CONFIRMED as taught (JOB-W01). Exactly
   *  those nodes — the furthest taught node is not proof that any earlier node was taught, and the
   *  planned `position` above is unchanged by teaching evidence. */
  taught: Set<number>;
}

/** Map the class's planned node ids onto a grade's curriculum. Unknown ids (from an older
 *  curriculum version) are ignored calmly — old plans stay valid, the map just moves on. */
export function classPosition(cur: GradeCurriculum, states: NodePlanState[]): ClassPosition {
  const bySeq = new Map(cur.nodes.map((n) => [n.id, n.seq]));
  const planned = new Set<number>();
  const approved = new Set<number>();
  const taught = new Set<number>();
  for (const s of states) {
    const seq = bySeq.get(s.node_id);
    if (seq === undefined) continue;
    planned.add(seq);
    if (s.approved) approved.add(seq);
    if ((s.taught ?? 0) > 0) taught.add(seq);
  }
  let position: CurriculumNode | null = null;
  for (const n of cur.nodes) {
    if (planned.has(n.seq) && (!position || n.seq > position.seq)) position = n;
  }
  return { position, planned, approved, taught };
}

export function nodeBySeq(cur: GradeCurriculum, seq: number): CurriculumNode | null {
  return cur.nodes.find((n) => n.seq === seq) ?? null;
}
